import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { api, getConfig } from './api'
import type { Actor, PublicConfig } from './types'
import { AppContext, useApp } from './app-context'
import { AppShell } from './components/AppShell'
import { Landing } from './pages/Landing'
import { AuthPage } from './pages/AuthPage'
import { CustomerDashboard } from './pages/CustomerDashboard'
import { NewInbound } from './pages/NewInbound'
import { Destinations } from './pages/Destinations'
import { OrderDetail } from './pages/OrderDetail'
import { StaffQueue } from './pages/StaffQueue'
import { Settings } from './pages/Settings'
import { AuthAction } from './pages/AuthAction'
import { Policy } from './pages/Policy'
import { AdminOrders } from './pages/AdminOrders'
import { AdminOrderDetail } from './pages/AdminOrderDetail'
import { AdminCustomers } from './pages/AdminCustomers'
import { AdminCustomerDetail } from './pages/AdminCustomerDetail'
import { InboundLabel } from './pages/InboundLabel'
import { StaffInboundLabels } from './pages/StaffInboundLabels'
import { PurchaseFlow } from './components/PurchaseFlow'

export function App(){
  const location=useLocation()
  const [config,setConfig]=useState<PublicConfig|null>(null)
  const [actor,setActor]=useState<Actor|null>(null)
  const [loading,setLoading]=useState(true)
  const refreshActor=async()=>{try{setActor(await api<Actor|null>('/api/me'))}catch{setActor(null)}}
  useEffect(()=>{void (async()=>{try{setConfig(await getConfig());await refreshActor()}finally{setLoading(false)}})()},[])
  const signOut=async()=>{await api('/api/auth/logout',{method:'POST'});setActor(null)}
  if(loading||!config)return <div className="boot"><span className="boot__route">rx <i/> routing</span><small>Preparing your route</small></div>
  return <AppContext.Provider value={{config,actor,refreshActor,signOut}}>
    <Routes>
      <Route path="/" element={<Landing/>}/>
      <Route path="/login" element={<AuthPage mode="login"/>}/>
      <Route path="/register" element={<PurchaseFlow key={location.pathname}><AuthPage mode="register"/></PurchaseFlow>}/>
      <Route path="/terms" element={<Policy slug="terms"/>}/>
      <Route path="/refunds" element={<Policy slug="refunds"/>}/>
      <Route path="/shipping" element={<Policy slug="shipping"/>}/>
      <Route path="/auth/action" element={<AuthAction/>}/>
      <Route path="/__/auth/action" element={<AuthAction/>}/>
      <Route element={<Protected><AppShell/></Protected>}>
        <Route path="/dashboard" element={<CustomerDashboard/>}/>
        <Route path="/inbounds/new" element={<PurchaseFlow key={location.pathname}><NewInbound/></PurchaseFlow>}/>
        <Route path="/destinations" element={<Destinations/>}/>
        <Route path="/orders/:orderID" element={<PurchaseFlow key={location.pathname}><OrderDetail/></PurchaseFlow>}/>
        <Route path="/orders/:orderID/inbound-label" element={<PurchaseFlow key={location.pathname}><InboundLabel/></PurchaseFlow>}/>
        <Route path="/settings" element={<Settings/>}/>
        <Route path="/staff" element={<StaffOnly><StaffQueue/></StaffOnly>}/>
        <Route path="/admin/orders" element={<AdminOnly><AdminOrders/></AdminOnly>}/>
        <Route path="/admin/orders/:orderID" element={<AdminOnly><AdminOrderDetail/></AdminOnly>}/>
        <Route path="/admin/customers" element={<AdminOnly><AdminCustomers/></AdminOnly>}/>
        <Route path="/admin/customers/:userID" element={<AdminOnly><AdminCustomerDetail/></AdminOnly>}/>
        <Route path="/staff/inbound-labels" element={<StaffOnly><StaffInboundLabels/></StaffOnly>}/>
      </Route>
      <Route path="*" element={<Navigate to={actor?'/dashboard':'/'} replace/>}/>
    </Routes>
  </AppContext.Provider>
}

function Protected({children}:{children:ReactNode}){const {actor}=useApp();const location=useLocation();return actor?children:<Navigate to="/login" state={{from:location.pathname}} replace/>}
function AdminOnly({children}:{children:ReactNode}){const {actor}=useApp();return actor?.isAdmin?children:<Navigate to="/dashboard" replace/>}
function StaffOnly({children}:{children:ReactNode}){const {actor}=useApp();return actor?.role==='staff'||actor?.role==='admin'?children:<Navigate to="/dashboard" replace/>}
